import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, CreditCard, LoaderCircle, LogOut, ShieldCheck } from "lucide-react";
import type { BlogBlock, ProjectedBlogPost } from "../shared/blog.ts";
import { api } from "./api.ts";
import { useLocale } from "./i18n.tsx";
import { Page, type Session } from "./Shell.tsx";
import { ShareInvitationCard } from "./ShareInvitationCard.tsx";

function InlineText({ text }: { text: string }) {
  const { href: localizedHref } = useLocale();
  const parts = useMemo(() => {
    const result: Array<{ text: string; href?: string }> = [];
    let rest = text;
    const pattern = /\[([^\]]+)]\(([^)]+)\)/;
    while (rest) {
      const match = rest.match(pattern);
      if (!match || match.index === undefined) {
        result.push({ text: rest.replace(/[*_`~]/g, "") });
        break;
      }
      if (match.index > 0) result.push({ text: rest.slice(0, match.index).replace(/[*_`~]/g, "") });
      const href = match[2].startsWith("/") || /^https:\/\//.test(match[2]) ? match[2] : undefined;
      result.push({ text: match[1].replace(/[*_`~]/g, ""), href });
      rest = rest.slice(match.index + match[0].length);
    }
    return result;
  }, [text]);
  return <>{parts.map((part, index) => part.href ? (
    <a key={`${part.href}-${index}`} href={part.href.startsWith("/") ? localizedHref(part.href) : part.href} {...(part.href.startsWith("https://") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{part.text}</a>
  ) : <span key={index}>{part.text}</span>)}</>;
}

function BlogBlocks({ blocks }: { blocks: BlogBlock[] }) {
  return <>{blocks.map((block, index) => {
    if (block.kind === "heading") return block.level === 2
      ? <h2 key={index}><InlineText text={block.text} /></h2>
      : <h3 key={index}><InlineText text={block.text} /></h3>;
    if (block.kind === "paragraph") return <p key={index}><InlineText text={block.text} /></p>;
    if (block.kind === "quote") return <blockquote key={index}><InlineText text={block.text} /></blockquote>;
    if (block.kind === "code") return <pre key={index}><code>{block.text}</code></pre>;
    const items = block.items.map((item) => <li key={item}><InlineText text={item} /></li>);
    return block.ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
  })}</>;
}

export function BlogIndexPage({ session }: { session: Session | null }) {
  const { locale, copy, href, isEnglish } = useLocale();
  const text = copy.content;
  const [posts, setPosts] = useState<ProjectedBlogPost[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setPosts(null);
    setError("");
    void api<{ posts: ProjectedBlogPost[] }>(`/api/blog?locale=${encodeURIComponent(locale)}`)
      .then((result) => setPosts(result.posts))
      .catch((caught) => setError(!isEnglish && caught instanceof Error ? caught.message : text.blogUnavailable));
  }, [isEnglish, locale, text.blogUnavailable]);
  return (
    <Page session={session} className="blog-page">
      <main id="main" className="blog-shell page-shell">
        <header className="blog-hero"><p className="eyebrow">{text.blogEyebrow}</p><h1>{text.blogTitle}</h1><p>{text.blogLead}</p></header>
        {error ? <div className="empty-card"><h2>{text.blogUnavailable}</h2><p>{error}</p><button className="button button-primary" type="button" onClick={() => window.location.reload()}>{copy.common.retry}</button></div>
          : posts === null ? <div className="loading-row"><LoaderCircle className="spinner" aria-hidden="true" />{text.blogLoading}</div>
          : posts.length === 0 ? <div className="empty-card"><h2>{text.blogEmpty}</h2><p>{text.blogEmptyBody}</p><a className="button button-primary" href={href("/play")}>{copy.common.play}</a></div>
          : <div className="article-grid">{posts.map((post) => <article key={`${post.locale}-${post.slug}`} className="article-card"><p>{new Date(post.publishedAt).toLocaleDateString(locale)} · {text.minutes(post.readingTime)}</p><h2><a href={href(`/blog/${post.slug}`)}>{post.title}</a></h2><div>{post.description}</div><a className="article-link" href={href(`/blog/${post.slug}`)}>{text.read}<ArrowRight aria-hidden="true" /></a></article>)}</div>}
      </main>
    </Page>
  );
}

export function BlogArticlePage({ session, slug }: { session: Session | null; slug: string }) {
  const { locale, copy, href, isEnglish } = useLocale();
  const text = copy.content;
  const [post, setPost] = useState<ProjectedBlogPost | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setPost(null);
    setError("");
    void api<{ post: ProjectedBlogPost }>(`/api/blog/${encodeURIComponent(slug)}?locale=${encodeURIComponent(locale)}`)
      .then((result) => setPost(result.post))
      .catch((caught) => setError(!isEnglish && caught instanceof Error ? caught.message : text.articleUnavailable));
  }, [isEnglish, locale, slug, text.articleUnavailable]);
  useEffect(() => {
    if (!post) return;
    const title = post.seoTitle;
    const canonical = `https://neckmoves.com/blog/${encodeURIComponent(slug)}${isEnglish ? "?locale=en" : ""}`;
    document.title = title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", post.description);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", title);
    document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", post.description);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", canonical);
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", canonical);
  }, [isEnglish, post, slug]);
  return (
    <Page session={session} className="article-page">
      <main id="main" className="article-shell page-shell">
        {error ? <div className="empty-card"><h1>{text.articleUnavailable}</h1><p>{error}</p><a className="button button-primary" href={href("/blog")}>{text.backBlog.replace("← ", "")}</a></div>
          : !post ? <div className="loading-row"><LoaderCircle className="spinner" aria-hidden="true" />{text.articleLoading}</div>
          : <article><a className="back-link" href={href("/blog")}>{text.backBlog}</a><header><p className="eyebrow">{text.guide}</p><h1>{post.title}</h1><p className="article-deck">{post.description}</p><div className="article-meta"><span>{post.author}</span><time dateTime={post.updatedAt}>{text.updated(new Date(post.updatedAt).toLocaleDateString(locale))}</time><span>{text.readingTime(post.readingTime)}</span></div></header><div className="article-body"><BlogBlocks blocks={post.blocks} /></div><footer className="article-footer"><p>{text.articleEnd}</p><a className="button button-primary" href={href("/play")}>{copy.common.play}</a></footer></article>}
      </main>
    </Page>
  );
}

type LegalDocument = {
  title: string;
  version: string;
  effective_at: string;
  composition: Array<{ sections: Array<{ id: string; title: string; body_markdown: string }> }>;
};

export function LegalPage({ session, slug }: { session: Session | null; slug: string }) {
  const { locale, copy, isEnglish } = useLocale();
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setDocument(null);
    setError("");
    void api<{
      document?: { document?: LegalDocument };
      supplement?: { supplement?: LegalDocument };
    }>(`/api/legal/${slug}?locale=${encodeURIComponent(locale)}`)
      .then((result) => setDocument(result.document?.document || result.supplement?.supplement || null))
      .catch((caught) => setError(!isEnglish && caught instanceof Error ? caught.message : isEnglish ? "The legal document is temporarily unavailable" : "法律文件暂时无法读取"));
  }, [isEnglish, locale, slug]);
  return (
    <Page session={session} className="legal-page">
      <main id="main" className="legal-shell page-shell">
        {error ? <div className="empty-card"><h1>{isEnglish ? "The legal document is temporarily unavailable" : "法律文件暂时无法读取"}</h1><p>{error}</p></div>
          : !document ? <div className="loading-row"><LoaderCircle className="spinner" aria-hidden="true" />{copy.content.officialLoading}</div>
          : <><header><p className="eyebrow">SZLK LTD</p><h1>{document.title}</h1><p>{isEnglish ? `Version ${document.version} · Effective ${document.effective_at}` : `版本 ${document.version} · 生效日期 ${document.effective_at}`}</p></header>{document.composition.flatMap((item) => item.sections).map((section) => <section key={section.id}><h2>{section.title}</h2>{section.body_markdown.split(/\n{2,}/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</section>)}</>}
      </main>
    </Page>
  );
}

type BillingPlan = {
  planId: string;
  label: string;
  labelZh: string;
  interval: "month" | "year" | "one_time" | "custom";
  currency: string | null;
  amountCents: number | null;
  featureKeys: string[];
  metadata?: Record<string, unknown> | null;
};

type BillingSnapshot = {
  loginRequired?: boolean;
  billing?: {
    subscriptions?: unknown[];
  } | null;
};

type BillingOverview = { catalog: { plans?: BillingPlan[] }; snapshot: BillingSnapshot };

function planPrice(plan: BillingPlan, locale: "zh-CN" | "en", fallback: string) {
  if (plan.amountCents === null || !plan.currency) return fallback;
  return new Intl.NumberFormat(locale, { style: "currency", currency: plan.currency.toUpperCase(), maximumFractionDigits: 2 }).format(plan.amountCents / 100);
}

export function PricingPage({ session }: { session: Session | null }) {
  const { locale, copy, href, isEnglish } = useLocale();
  const text = copy.content;
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    void api<BillingOverview>("/api/billing/overview").then(setOverview).catch(() => setOverview({ catalog: { plans: [] }, snapshot: {} }));
    const checkout = new URLSearchParams(window.location.search).get("checkout");
    if (checkout === "success") setMessage(text.paymentSync);
    if (checkout === "cancelled") setMessage(text.paymentCancelled);
  }, [text.paymentCancelled, text.paymentSync]);
  async function checkout(plan: BillingPlan) {
    if (!session) {
      window.location.assign(href(`/login?return_to=${encodeURIComponent(href("/pricing"))}`));
      return;
    }
    setBusy(plan.planId);
    setMessage("");
    try {
      const result = await api<{ checkout?: { url?: string } }>("/api/billing/checkout", { method: "POST", body: JSON.stringify({ planId: plan.planId }) });
      if (!result.checkout?.url) throw new Error(text.paymentUnavailable);
      window.location.assign(result.checkout.url);
    } catch (error) {
      setMessage(!isEnglish && error instanceof Error ? error.message : text.paymentUnavailable);
      setBusy("");
    }
  }
  const plans = overview?.catalog.plans || [];
  return (
    <Page session={session} className="pricing-page">
      <main id="main" className="pricing-shell page-shell">
        <header><p className="eyebrow">{text.priceEyebrow}</p><h1>{text.priceTitle}</h1><p>{text.priceLead}</p></header>
        {message ? <div className="form-message neutral" role="status">{message}</div> : null}
        <div className="pricing-grid">
          <article className="price-card featured"><p className="price-label">{text.free}</p><h2>{text.basic}</h2><div className="price">{new Intl.NumberFormat(locale, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(0)}<span>{text.forever}</span></div><ul><li><Check aria-hidden="true" />{text.featureGame}</li><li><Check aria-hidden="true" />{text.featureLocal}</li><li><Check aria-hidden="true" />{text.featureNoAccount}</li></ul><a className="button button-primary" href={href("/play")}>{copy.common.play}</a></article>
          {plans.map((plan) => {
            const featureField = isEnglish ? plan.metadata?.featuresEn : plan.metadata?.featuresZh;
            const describedFeatures = Array.isArray(featureField) ? featureField.map(String).filter(Boolean).slice(0, 5) : [...text.fallbackFeatures];
            return <article className="price-card" key={plan.planId}><p className="price-label">{text.upgrade}</p><h2>{isEnglish ? plan.label : plan.labelZh || plan.label}</h2><div className="price">{planPrice(plan, locale, text.beforeCheckout)}<span>{text.intervals[plan.interval]}</span></div><ul>{describedFeatures.map((feature) => <li key={feature}><Check aria-hidden="true" />{feature}</li>)}</ul><button className="button button-secondary" type="button" disabled={Boolean(busy)} onClick={() => void checkout(plan)}>{busy === plan.planId ? <><LoaderCircle className="spinner" aria-hidden="true" />{text.openPlan}</> : text.selectPlan}</button></article>;
          })}
        </div>
        {overview && plans.length === 0 ? <p className="pricing-footnote">{text.plansPreparing}</p> : null}
      </main>
    </Page>
  );
}

export function AccountPage({ session, onSession }: { session: Session | null; onSession: (session: Session | null) => void }) {
  const { copy, href, isEnglish } = useLocale();
  const text = copy.content;
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [billing, setBilling] = useState<"loading" | "available" | "free" | "unavailable">("loading");
  useEffect(() => {
    if (!session) {
      setBilling("free");
      return;
    }
    void api<BillingOverview>("/api/billing/overview")
      .then((overview) => setBilling((overview.snapshot.billing?.subscriptions?.length || 0) > 0 ? "available" : "free"))
      .catch(() => setBilling("unavailable"));
  }, [session]);
  if (!session) {
    return <Page session={null} className="account-page"><main id="main" className="account-shell page-shell"><div className="empty-card"><h1>{text.accountLoginTitle}</h1><p>{text.accountLoginLead}</p><a className="button button-primary" href={href("/login?return_to=/account")}>{copy.common.login}</a></div></main></Page>;
  }
  async function openPortal() {
    setBusy(true);
    setMessage("");
    try {
      const result = await api<{ portal?: { url?: string } }>("/api/billing/portal", { method: "POST", body: "{}" });
      if (!result.portal?.url) throw new Error(text.portalUnavailable);
      window.location.assign(result.portal.url);
    } catch (error) {
      setMessage(!isEnglish && error instanceof Error ? error.message : text.portalUnavailable);
      setBusy(false);
    }
  }
  async function logout() {
    await api("/api/auth/logout/current", { method: "POST", body: "{}" });
    onSession(null);
    window.location.assign(href("/"));
  }
  return (
    <Page session={session} className="account-page">
      <main id="main" className="account-shell page-shell">
        <header><p className="eyebrow">{text.accountEyebrow}</p><h1>{session.name || text.defaultUser}</h1><p>{session.email}</p></header>
        {message ? <div className="form-message error" role="alert">{message}</div> : null}
        <ShareInvitationCard name={session.name || ""} />
        <section className="account-card"><ShieldCheck aria-hidden="true" /><div><h2>{text.privacyTitle}</h2><p>{text.privacyBody}</p></div></section>
        <section className="account-card"><CreditCard aria-hidden="true" /><div><h2>{text.subscription}</h2>{billing === "loading" ? <p>{text.subscriptionLoading}</p>
          : billing === "available" ? <><p>{text.subscriptionAvailable}</p><button className="button button-secondary" type="button" disabled={busy} onClick={() => void openPortal()}>{busy ? text.openPlan : text.manage}</button></>
          : billing === "free" ? <><p>{text.subscriptionFree}</p><a className="button button-secondary" href={href("/pricing")}>{text.viewPlans}</a></>
          : <><p>{text.subscriptionUnavailable}</p><button className="button button-secondary" type="button" onClick={() => window.location.reload()}>{copy.common.retry}</button></>}</div></section>
        <button className="button button-quiet logout-button" type="button" onClick={() => void logout()}><LogOut aria-hidden="true" />{text.logout}</button>
      </main>
    </Page>
  );
}

export function AboutPage({ session }: { session: Session | null }) {
  const { copy } = useLocale();
  const text = copy.content;
  return (
    <Page session={session} className="about-page">
      <main id="main" className="about-shell page-shell">
        <header><p className="eyebrow">{text.aboutEyebrow}</p><h1>{text.aboutTitle}</h1></header>
        <section><h2>{text.problem}</h2><p>{text.problemBody}</p></section>
        <section><h2>{text.device}</h2><p>{text.deviceBody}</p></section>
        <section><h2>{text.safety}</h2><p>{text.safetyBody}</p></section>
      </main>
    </Page>
  );
}

export function NotFoundPage({ session }: { session: Session | null }) {
  const { copy, href } = useLocale();
  return <Page session={session}><main id="main" className="not-found page-shell"><p className="eyebrow">404</p><h1>{copy.content.notFoundTitle}</h1><p>{copy.content.notFoundBody}</p><a className="button button-primary" href={href("/")}>{copy.common.backHome}</a></main></Page>;
}
